import { percent } from "@amcharts/amcharts5";
import { IPieChartSettings, PieChart, PieSeries } from "@amcharts/amcharts5/percent";
import { XYChart } from "@amcharts/amcharts5/xy";
import { forIn, map, pick } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

type DoughnutDataItem = {
  [key: string]: string | number | { categoryField: string; valueField: string };
};

export default class extends BaseChart {
  valueField!: string;
  categoryField!: string;

  initChart() {
    this.root.container.children.clear();
    this.createChart();

    this.createSeries(this.settings.displayMetric);
    this.showLegend(
      map(this.data, this.settings.decompositionMetric) as string[],
      (this.chart as XYChart).series.values[0].dataItems as LegendData,
      false
    );

    this.categoryField = this.settings.decompositionMetric;
    this.valueField = this.settings.displayMetric;
  }

  protected createChart() {
    const holeSize = Number(this.settings.holeSize);
    const innerRadius = Number.isNaN(holeSize) ? this.settings.holeSize.default : holeSize;

    const chartSettings: IPieChartSettings = {
      ...chartInitialSettings,
      radius: percent(100),
      y: percent(0),
      layout: this.layout,
    };

    if (innerRadius !== 0) {
      chartSettings.innerRadius = percent(innerRadius);
    }

    this.chart = this.root.container.children.push(PieChart.new(this.root, chartSettings));
  }

  protected createSeries(seriesName: string) {
    const series = (this.chart as PieChart).series.push(
      PieSeries.new(this.root, {
        name: seriesName,
        valueField: this.valueField,
        categoryField: this.categoryField,
      })
    );

    // Turn off shifting on click.
    series.slices.template.set("toggleKey", "none");
    // Turn off scaling on hover.
    series.slices.template.states.create("hover", { scale: 1 });

    this.createTooltip(series as TooltipSeries, (target) => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext;

      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });

    series.get("colors")?.set("colors", this.palette);

    series.labels.template.setAll({
      visible: false,
      forceHidden: true,
    });
    series.ticks.template.setAll({
      visible: false,
      forceHidden: true,
    });
    // Use this for future data-label tasks
    // series.set("alignLabels", true);
    // series.labels.template.setAll({
    //     visible: true,
    //     forceHidden: false,
    //     text: "{value.formatNumber('#,##0')}", // apply actual formatter
    //     populateText: true,
    // });
    // series.ticks.template.setAll({
    //     visible: true,
    //     forceHidden: false,
    // });
    //
    series.data.setAll(this.data);
  }

  protected mapData(data: ChartDataRecord[]) {
    if (!data || !Object.keys(data)) {
      return [];
    }

    this.categoryField = this.settings.decompositionMetric;
    this.valueField = this.settings.displayMetric;
    data = data.map((item) => pick(item, [this.categoryField, this.valueField]));

    if (!this.categoryField || !this.valueField) {
      return [];
    }

    let groupedData: Record<string, number> = {};

    switch (this.categoryField) {
      default:
        groupedData = data.reduce((acc: Record<string, number>, curr) => {
          return {
            ...acc,
            [(curr[this.categoryField] || "null") as string]:
              ((acc[this.categoryField] || 0) as number) + (curr[this.valueField] as number) || 0,
          };
        }, {});
        break;
    }
    let resultData: DoughnutDataItem[] = Object.keys(groupedData)
      .map((key: string) => ({
        [this.categoryField]: key,
        [this.valueField]: groupedData[key],
        tooltipValues: {
          categoryField: this.formatters[this.categoryField](key),
          valueField: this.formatters[this.valueField](groupedData[key]),
        },
      }))
      .sort((a, b) => (b[this.valueField] as number) - (a[this.valueField] as number));

    resultData = this.showItems(resultData, this.categoryField, this.settings.displayMetric);

    return resultData;
  }
}
